import React, { FC, useCallback, useEffect, useRef, useState } from "react";

import { Box, MenuItem, MenuList, Select as SelectLib, SelectProps } from "@mui/material";

import { ClosedSortIcon, OpenSortIcon, SelectArrowDownIcon, SortCheckmarkIcon } from "@assets/svg";

import { SelectStack, SortButton } from "@pages/Catalog/components";
import { ISortBy } from "@pages/Catalog/components/utils";

import { useIsDesktop } from "@hooks/layout";

import uniqueId from "lodash/uniqueId";
import { bindToggle } from "material-ui-popup-state";
import { usePopupState } from "material-ui-popup-state/hooks";

import { Menu } from "../Menu";

type TSelect = SelectProps & {
  data: ISortBy[];
  handleSelect: (data: ISortBy) => void;
  isFullWidth?: boolean;
};

const paperProps = (open: boolean, isFullWidth?: boolean, isDesktop?: boolean) => ({
  sx: {
    margin: "8px 0 0 0",
    border: "1px solid #FFDCA8",
    borderTop: isDesktop ? "0" : "1px solid #FFDCA8",
    borderRadius: "4px",
    borderTopLeftRadius: isDesktop ? "0" : "4px",
    borderTopRightRadius: isDesktop ? "0" : "4px",
    backgroundColor: "#4B3727",
    boxShadow: "none",
    fontFamily: "Raleway",
    transition: open ? undefined : "none !important",
    ".MuiMenuItem-root": {
      minHeight: isFullWidth ? "auto" : undefined,
      paddingLeft: "16px",
      paddingRight: "16px",
    },
    ".MuiList-root": {
      maxHeight: isFullWidth ? "260px" : undefined,
    },
  },
});
export const Select: FC<TSelect> = ({ data, handleSelect, isFullWidth, MenuProps: externalMenuProps, ...restProps }) => {
  const isDesktop = useIsDesktop();
  const paperRef = useRef<HTMLDivElement | null>(null);
  const selectRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState<boolean>(false);
  const [isPaperAppear, setIsPaperAppear] = useState(false);

  const popupState = usePopupState({ variant: "popper", popupId: "select-popover" });

  const paperRefCallback = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      paperRef.current = node;
      setIsPaperAppear(true);
    }
  }, []);

  const setPaperCords = () => {
    if (paperRef.current) {
      const { x, y } = selectRef.current.getBoundingClientRect();
      paperRef.current.style.left = x + "px";
      paperRef.current.style.top = y + (selectRef.current ? selectRef.current.clientHeight : 0) + "px";
    }
  };

  useEffect(() => {
    setPaperCords();
  }, [isPaperAppear]);

  useEffect(() => {
    const handleScroll = () => {
      if (isDesktop) {
        setPaperCords();
        setOpen(false);
        setIsPaperAppear(false);
      }
    };

    document.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, [isDesktop]);

  if (isDesktop || isFullWidth) {
    return (
      <SelectStack>
        <SelectLib
          ref={selectRef}
          {...restProps}
          IconComponent={SelectArrowDownIcon}
          MenuProps={{
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
            },
            sx: {
              zIndex: 999,
              ...externalMenuProps?.sx,
            },
            PaperProps: {
              sx: {
                ...paperProps(open, isFullWidth, isDesktop).sx,
                mt: "-3px",
                ...externalMenuProps?.PaperProps?.sx,
              },

              ref: paperRefCallback,
            },
            marginThreshold: null,
            disableScrollLock: isDesktop,
          }}
          open={open}
          onClose={() => {
            setOpen(false);
            setIsPaperAppear(false);
          }}
          onOpen={() => setOpen(true)}
          sx={{
            "& .MuiSelect-select": {
              fontFamily: "Raleway",
              pt: "10px !important",
              pb: "10px !important",
            },
            "& fieldset": {
              padding: "10px !important",
              border: "none",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "1px solid #FFDCA8",
              borderRadius: "4px",
            },
          }}
        >
          {data.map(({ value, title }, id) => (
            <MenuItem
              key={uniqueId(`${id}-sortSelect-`)}
              value={value}
              sx={{
                marginLeft: "8px",
                marginRight: "8px",

                fontSize: "14px",
                justifyContent: "space-between",
                fontFamily: "Raleway",
                color: "textColor.common",
                "&.Mui-selected": {
                  color: "textColor.header",
                  backgroundColor: "#4b3727 !important",
                },
                "&.Mui-selected div": {
                  display: "block",
                },
                "&:hover": {
                  color: "textColor.header",
                  borderRadius: "6px !important",
                  backgroundColor: "#5C412A !important",
                },
              }}
              onClick={() => handleSelect(data[id])}
            >
              {title}
              <Box
                sx={{
                  display: "none",
                }}
              >
                <SortCheckmarkIcon />
              </Box>
            </MenuItem>
          ))}
        </SelectLib>
      </SelectStack>
    );
  }

  return (
    <>
      <SortButton {...bindToggle(popupState)}>{popupState.isOpen ? <OpenSortIcon /> : <ClosedSortIcon />}</SortButton>
      <Menu placement={"bottom-start"} popupState={popupState} paperProps={paperProps(open)}>
        <MenuList>
          {data.map(({ value, title }, id) => (
            <MenuItem
              key={uniqueId(`${id}-sortSelect-`)}
              value={value}
              sx={{
                paddingLeft: "6px",
                paddingRight: "6px",
                fontSize: "14px",
                justifyContent: "space-between",
                fontFamily: "Raleway",
                color: "textColor.common",
                "&.Mui-selected": {
                  color: "textColor.header",
                  backgroundColor: "#4b3727 !important",
                },
                "&.Mui-selected div": {
                  display: "block",
                },
                "&:hover": {
                  color: "textColor.header",
                  borderRadius: "6px !important",
                  backgroundColor: "#5C412A !important",
                },
              }}
              onClick={() => {
                handleSelect(data[id]);
                popupState.close();
              }}
            >
              {title}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </>
  );
};
